export const navLinks = [
  { to: '/', label: 'Home' },
  { to: '/help-topics', label: 'Help Topics' },
  { to: '/about', label: 'About' },
  { to: '/contact', label: 'Contact' },
];

export const hero = {
  eyebrow: 'Independent genealogy software help',
  title: 'Chat with a real person about your Family Tree Maker problem',
  lead: 'Stuck on an install, a sync that will not finish, or a tree that will not open? Start a chat and get step-by-step help in plain English — day or night.',
  points: ['Windows and Mac', 'No scripts, no jargon', 'Help until it works'],
};

export const stats = [
  { value: '24/7', label: 'Chat & phone help' },
  { value: '< 2 min', label: 'Typical wait to chat' },
  { value: 'Win · Mac', label: 'Both platforms' },
  { value: '1-on-1', label: 'Personal guidance' },
];

export const services = [
  {
    icon: 'download',
    title: 'Download & Install',
    body: 'Get the installer running, activate your copy, and open your first tree without errors.',
  },
  {
    icon: 'sync',
    title: 'Sync & TreeVault',
    body: 'Sort out stuck syncs, conflict messages, and TreeVault backups that refuse to finish.',
  },
  {
    icon: 'upgrade',
    title: 'Upgrade Help',
    body: 'Move from an older edition to the latest one while keeping every person, source, and note.',
  },
  {
    icon: 'laptop',
    title: 'New Computer Moves',
    body: 'Transfer your trees and linked media to a new PC or Mac and relink anything that goes missing.',
  },
  {
    icon: 'photo',
    title: 'Media & Photos',
    body: 'Find missing pictures, fix broken media links, and organise scans so they stay attached.',
  },
  {
    icon: 'repair',
    title: 'Errors & Crashes',
    body: 'Freezes, “file in use” messages, and trees that will not open — we help you track down the cause.',
  },
];

export const steps = [
  {
    title: 'Start a chat',
    body: 'Click any chat button or call us. Tell us your version, your computer, and what is going wrong.',
  },
  {
    title: 'Follow along together',
    body: 'An agent walks you through each click and checks the result with you before moving on.',
  },
  {
    title: 'Back to your research',
    body: 'We confirm your tree opens, your backup is fresh, and everything is where you expect it.',
  },
];

export const topics = [
  {
    slug: 'install',
    title: 'Installing Family Tree Maker',
    summary: 'Common problems during download, setup, and activation — and what to try first.',
    tips: [
      'Close other programs and antivirus pop-ups before running the installer.',
      'Right-click the installer on Windows and choose “Run as administrator”.',
      'On Mac, allow the app in System Settings → Privacy & Security if it is blocked.',
      'Keep your order email handy — activation asks for the account you purchased with.',
    ],
  },
  {
    slug: 'sync',
    title: 'Sync and TreeVault',
    summary: 'When a sync hangs, fails, or creates duplicate people, these checks usually help.',
    tips: [
      'Make a local backup before retrying a failed sync.',
      'Check that you are signed in to the same account on every computer.',
      'Let a large first sync finish fully — interrupting it often causes conflicts.',
      'If conflicts appear, review them one at a time instead of accepting all.',
    ],
  },
  {
    slug: 'upgrade',
    title: 'Upgrading to a New Version',
    summary: 'How to move to a newer edition without losing data or settings.',
    tips: [
      'Back up every tree from the old version first and note where the backups are saved.',
      'Install the new version before uninstalling the old one.',
      'Open each tree in the new version and let it convert — do not cancel midway.',
      'Spot-check a few people, sources, and photos after converting.',
    ],
  },
  {
    slug: 'new-computer',
    title: 'Moving to a New Computer',
    summary: 'A safe order of steps for transferring trees and media to a new machine.',
    tips: [
      'Create a backup that includes media files, then copy it to a USB drive or cloud folder.',
      'Install Family Tree Maker on the new computer and sign in.',
      'Restore from the backup rather than copying the raw tree file.',
      'Use the media relink tool if any photos show as missing.',
    ],
  },
  {
    slug: 'media',
    title: 'Missing Photos and Media',
    summary: 'Why pictures disappear from a tree and how to reconnect them.',
    tips: [
      'Media goes “missing” when the original file is moved or renamed.',
      'Keep all tree media inside one main folder that you never rename.',
      'Use Find Missing Media to search a drive for the original files.',
      'Include media when you back up so the links travel with the tree.',
    ],
  },
  {
    slug: 'errors',
    title: 'Crashes and Error Messages',
    summary: 'First steps when the program freezes, crashes, or will not open a tree.',
    tips: [
      'Restart the computer and open the program before anything else.',
      'Try opening a different tree to see whether the problem is the file or the program.',
      'Use the compact or repair option on the affected tree after making a backup.',
      'Write down the exact error text — it helps us find the cause quickly.',
    ],
  },
];

export const reviews = [
  {
    text: 'My sync had been failing for weeks. The chat agent was patient, explained what was happening, and had it working in about twenty minutes.',
    name: 'Carol T.',
    place: 'Columbus, OH',
  },
  {
    text: 'I bought a new Mac and was terrified of losing thirty years of research. They talked me through the backup and restore and every photo came across.',
    name: 'James P.',
    place: 'Raleigh, NC',
  },
  {
    text: 'Clear, friendly, and no rushing. I finally understand how my backups work and where they are saved.',
    name: 'Linda M.',
    place: 'Tucson, AZ',
  },
];

export const faqs = [
  {
    q: 'Are you Family Tree Maker or its publisher?',
    a: 'No. We are an independent support service and are not affiliated with, endorsed by, or sponsored by Family Tree Maker or its publisher. For licence purchases or account billing, contact the publisher directly.',
  },
  {
    q: 'What can you help with?',
    a: 'Installation, activation, upgrades, sync and TreeVault, moving to a new computer, missing media, and error messages on Windows and Mac.',
  },
  {
    q: 'How fast can I get help?',
    a: 'Chat is usually answered within a couple of minutes, any time of day. You can also call us or send the form and we will get back to you.',
  },
  {
    q: 'Will I lose any of my tree data?',
    a: 'We always start by helping you make a fresh backup, so your tree can be restored if anything goes wrong.',
  },
  {
    q: 'Do you need remote access to my computer?',
    a: 'Most issues are solved by guiding you step by step over chat or phone. We never ask for your passwords.',
  },
];
